/* ============================================================
   MIXMIX CUSTOMIZATION
   Feature: MixMix Shop The Look Finder
   File: assets/mixmix-look-finder.js
   Date: 2026-09-22

   Purpose:
   Filters native Pebble Shop The Look sections based on:
   - gender
   - age / look selector
   - moment

   Flow:
   1. Gender + age selected
   2. Moment selected
   3. Matching Pebble Shop The Look sections are shown
   4. Changing gender or age resets the moment/results
   5. User chooses a moment again
   6. New matching looks are shown

   Events:
   - mixmix:look-selection-change
     Parent selection changed: gender / age

   - mixmix:look-filter-change
     Complete selection: gender / age / moment

   Standalone MixMix asset.
   Does not modify Pebble core JavaScript.

   REVIEW AFTER THEME UPDATE:
   Check compatibility with Pebble Shop The Look markup.
   ============================================================ */

(() => {

  const ITEM_SELECTOR =
    '[data-mixmix-look-finder-item]';

  let currentSelection = null;


  /* ==========================================================
     GET ALL LOOK FINDER ITEMS
     ========================================================== */

  function getItems() {

    return document.querySelectorAll(
      ITEM_SELECTOR
    );

  }


  /* ==========================================================
     GET SHOPIFY SECTION WRAPPER

     The MixMix data attributes live on the native Pebble
     Shop The Look section.

     Shopify wraps every section in:
     .shopify-section

     We hide that complete wrapper so no empty spacing remains.
     ========================================================== */

  function getSectionWrapper(item) {

    return (
      item.closest('.shopify-section') ||
      item
    );

  }


  /* ==========================================================
     SHOW / HIDE INDIVIDUAL LOOK
     ========================================================== */

  function setVisibility(item, visible) {

    const wrapper =
      getSectionWrapper(item);

    if (!wrapper) return;


    if (visible) {

      wrapper.hidden = false;

      wrapper.removeAttribute(
        'aria-hidden'
      );

      wrapper.classList.add(
        'mixmix-look-is-visible'
      );

      wrapper.classList.remove(
        'mixmix-look-is-hidden'
      );

    } else {

      wrapper.hidden = true;

      wrapper.setAttribute(
        'aria-hidden',
        'true'
      );

      wrapper.classList.add(
        'mixmix-look-is-hidden'
      );

      wrapper.classList.remove(
        'mixmix-look-is-visible'
      );

    }

  }


  /* ==========================================================
     HIDE ALL LOOK FINDER ITEMS
     ========================================================== */

  function hideAll() {

    getItems().forEach((item) => {

      setVisibility(
        item,
        false
      );

    });

  }


  /* ==========================================================
     DOES LOOK MATCH CURRENT SELECTION?
     ========================================================== */

  function matchesSelection(
    item,
    selection
  ) {

    if (
      !item ||
      !selection
    ) {
      return false;
    }


    const itemGender =
      item.dataset.mixmixGender;

    const itemAge =
      item.dataset.mixmixAge;

    const itemMoment =
      item.dataset.mixmixMoment;


    return (
      itemGender === selection.gender &&
      itemAge === selection.age &&
      itemMoment === selection.moment
    );

  }


  /* ==========================================================
     SAVE GLOBAL STATE
     ========================================================== */

  function saveGlobalState(selection) {

    window.MixMixShopTheLook =
      window.MixMixShopTheLook || {};

    window.MixMixShopTheLook.selection = {
      ...selection
    };

  }


  /* ==========================================================
     RESET RESULTS

     Used whenever gender or age changes.

     Example:

     boys + baby + elke-dag
            ↓
     switch to girls
            ↓
     current looks disappear
            ↓
     moment is no longer part of the global selection
     ========================================================== */

  function resetResults(
    gender,
    age,
    ageLabel
  ) {

    currentSelection = null;

    hideAll();


    saveGlobalState({
      gender: gender || null,
      age: age || null,
      ageLabel: ageLabel || null
    });


    /*
      Optional event for future components.
    */

    document.dispatchEvent(
      new CustomEvent(
        'mixmix:look-results-reset',
        {
          detail: {
            gender: gender || null,
            age: age || null,
            ageLabel: ageLabel || null
          }
        }
      )
    );

  }


  /* ==========================================================
     APPLY COMPLETE FILTER
     ========================================================== */

  function applyFilter(selection) {

    if (
      !selection ||
      !selection.gender ||
      !selection.age ||
      !selection.moment
    ) {

      hideAll();
      return;

    }


    currentSelection = {
      ...selection
    };


    let visibleCount = 0;


    getItems().forEach((item) => {

      const visible =
        matchesSelection(
          item,
          selection
        );


      setVisibility(
        item,
        visible
      );


      if (visible) {
        visibleCount += 1;
      }

    });


    /*
      Store complete active state.
    */

    saveGlobalState(
      selection
    );


    /*
      Broadcast results.

      Useful later for:
      - result counter
      - empty state
      - breadcrumbs
      - analytics
      - scroll behaviour
    */

    document.dispatchEvent(
      new CustomEvent(
        'mixmix:look-results-updated',
        {
          detail: {
            ...selection,
            count: visibleCount
          }
        }
      )
    );

  }


  /* ==========================================================
     PARENT SELECTION CHANGED
     Gender and/or age has been selected.

     IMPORTANT:
     If a complete result was active and either gender or age
     changes, the old moment/result combination becomes invalid.

     Therefore:
     - hide current looks immediately
     - clear current moment
     - wait for a new moment selection
     ========================================================== */

  document.addEventListener(
    'mixmix:look-selection-change',
    (event) => {

      if (!event.detail) return;


      const nextGender =
        event.detail.gender || null;

      const nextAge =
        event.detail.age || null;

      const nextAgeLabel =
        event.detail.ageLabel || null;


      /*
        Determine previous parent state.

        First use currentSelection if a complete selection exists.
        Otherwise check the globally stored state.
      */

      const globalSelection =
        window.MixMixShopTheLook &&
        window.MixMixShopTheLook.selection
          ? window.MixMixShopTheLook.selection
          : null;


      const previousGender =
        currentSelection?.gender ||
        globalSelection?.gender ||
        null;

      const previousAge =
        currentSelection?.age ||
        globalSelection?.age ||
        null;


      const genderChanged =
        previousGender !== null &&
        previousGender !== nextGender;

      const ageChanged =
        previousAge !== null &&
        previousAge !== nextAge;


      /*
        If gender or age changes:
        reset moment + results.
      */

      if (
        genderChanged ||
        ageChanged
      ) {

        resetResults(
          nextGender,
          nextAge,
          nextAgeLabel
        );

        return;

      }


      /*
        No complete selection yet.

        Save gender + age so other MixMix components know
        the current parent selection.
      */

      if (!currentSelection) {

        saveGlobalState({
          gender: nextGender,
          age: nextAge,
          ageLabel: nextAgeLabel
        });

        hideAll();

      }

    }
  );


  /* ==========================================================
     COMPLETE FILTER RECEIVED FROM MOMENTS
     ========================================================== */

  document.addEventListener(
    'mixmix:look-filter-change',
    (event) => {

      if (!event.detail) return;

      applyFilter(
        event.detail
      );

    }
  );


  /* ==========================================================
     INITIAL STATE
     ========================================================== */

  function init() {

    const existing =
      window.MixMixShopTheLook &&
      window.MixMixShopTheLook.selection;


    /*
      Restore a complete selection if one already exists.
    */

    if (
      existing &&
      existing.gender &&
      existing.age &&
      existing.moment
    ) {

      applyFilter(
        existing
      );

      return;

    }


    /*
      No complete selection:
      all Look Finder sections remain hidden.
    */

    hideAll();

  }


  /* ==========================================================
     DOM READY
     ========================================================== */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );

  } else {

    init();

  }


  /* ==========================================================
     SHOPIFY THEME EDITOR

     Shopify can reload individual sections while editing.

     Reapply the current filter after such a reload.
     ========================================================== */

  document.addEventListener(
    'shopify:section:load',
    () => {

      window.requestAnimationFrame(
        () => {

          /*
            Active complete selection.
          */

          if (currentSelection) {

            applyFilter(
              currentSelection
            );

            return;

          }


          /*
            Check global state.
          */

          const existing =
            window.MixMixShopTheLook &&
            window.MixMixShopTheLook.selection;


          if (
            existing &&
            existing.gender &&
            existing.age &&
            existing.moment
          ) {

            applyFilter(
              existing
            );

          } else {

            hideAll();

          }

        }
      );

    }
  );

})();